import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";
import logger from "../lib/logger";
import { 
  Sparkles, Heart, History, ArrowLeft, 
  Play, Trash2, Star
} from "lucide-react";
import { AppMenu } from "../components/AppMenu";
import { SiteFooter } from "../components/SiteFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_ICONS = {
  quiet_the_worry: "🧘",
  overcome_overwhelm: "🌊",
  dial_down_tension: "💆",
  healing_old_stories: "📖",
  beat_stage_fright: "🎭",
  fear_of_sleeplessness: "🌙",
  shake_off_sadness: "🌤",
  boost_self_worth: "💪",
  hold_boundaries: "🛡",
  relief_from_grief: "🕊",
  calm_your_anger: "🔥",
  enhance_performance: "🎯",
  new_habit_new_you: "🌱"
};

const FavoritesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites`, { withCredentials: true });
      setFavorites(response.data);
    } catch (error) {
      logger.error("Failed to fetch favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (type) => {
    try {
      await axios.delete(`${API}/favorites/${type}`, { withCredentials: true });
      toast.success("Removed from favorites");
      fetchFavorites();
    } catch (error) {
      logger.error("Failed to remove favorite:", error);
      toast.error("Failed to remove from favorites");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF8F1] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3090A8]"></div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen relative isolate"
      data-testid="favorites-page"
      style={{
        backgroundImage: "url('/images/dashboard/favorites-wildflowers.webp')",
        backgroundAttachment: "fixed",
        backgroundSize: "cover",
        backgroundPosition: "center 55%",
      }}
    >
      {/* Cream-tinted overlay so wildflowers sit softly behind the UI. */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-[#FBF8F1]/45 backdrop-blur-[2px]"
      />
      {/* Header */}
      <header className="glass-header border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#3090A8] rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="font-outfit font-semibold text-xl text-[#1B2349]">Bilateral Calm</span>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <Link 
              to="/dashboard" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-dashboard"
            >
              Dashboard
            </Link>
            <Link 
              to="/history" 
              className="text-[#718096] hover:text-[#3090A8] transition-colors"
              data-testid="nav-history"
            >
              History
            </Link>
            <Link 
              to="/favorites" 
              className="text-[#1B2349] font-medium hover:text-[#3090A8] transition-colors"
              data-testid="nav-favorites"
            >
              Favorites
            </Link>
          </nav>

          <div className="flex items-center gap-4">
            {user?.picture && (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="w-8 h-8 rounded-full"
              />
            )}
            <AppMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 pb-24 app-prose">
        {/* Title box — sits on top of the wildflowers wallpaper. */}
        <div
          className="mb-8 bg-white/85 backdrop-blur-md rounded-3xl px-6 sm:px-8 py-6 sm:py-7 shadow-[0_8px_30px_rgba(27,35,73,0.08)] border border-white"
          data-testid="favorites-hero"
        >
          <p className="text-[#3090A8] text-xs uppercase tracking-[0.2em] font-medium mb-2">
            Your favorites
          </p>
          <h2 className="font-outfit font-semibold text-[#1B2349] text-2xl sm:text-3xl tracking-tight leading-tight">
            The practices that bloom for you.
          </h2>
        </div>

        {/* Back button and title */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#1B2349]" />
          </button>
          <div>
            <h1 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349]">
              Favorites
            </h1>
            <p className="text-[#718096]">
              {favorites.length} saved practice{favorites.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>

        {/* Favorites List */}
        {favorites.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 text-center">
            <div className="w-16 h-16 bg-[#FEF3E7] rounded-full flex items-center justify-center mx-auto mb-4">
              <Star className="w-8 h-8 text-[#E2B887]" />
            </div>
            <h3 className="font-outfit font-medium text-lg text-[#1B2349] mb-2">
              No Favorites Yet
            </h3>
            <p className="text-[#718096] mb-6">
              Add interventions to your favorites for quick access.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
              data-testid="browse-btn"
            >
              Browse Practices
            </Button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-6">
            {favorites.map((item, index) => (
              <div
                key={item.favorite_id}
                className={`bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 card-hover animate-fade-in-up stagger-${(index % 5) + 1}`}
                data-testid={`favorite-item-${item.intervention_type}`}
              >
                <div 
                  className="h-32 bg-cover bg-center relative"
                  style={{ backgroundImage: `url(${item.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <button
                    onClick={() => removeFavorite(item.intervention_type)}
                    className="absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                    data-testid={`remove-favorite-btn-${item.intervention_type}`}
                  >
                    <Trash2 className="w-4 h-4 text-[#D98880]" />
                  </button>
                  <div className="absolute bottom-3 left-3">
                    <span className="text-3xl">{INTERVENTION_ICONS[item.intervention_type]}</span>
                  </div>
                </div>
                
                <div className="p-5">
                  <h3 className="font-outfit font-medium text-lg text-[#1B2349] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-[#718096] text-sm mb-4 line-clamp-2">
                    {item.description}
                  </p>
                  
                  <Button
                    onClick={() => navigate(`/intervention/${item.intervention_type}`)}
                    className="w-full btn-primary"
                    data-testid={`start-btn-${item.intervention_type}`}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-100 px-6 py-3">
        <div className="flex items-center justify-around">
          <Link 
            to="/dashboard"
            className="flex flex-col items-center gap-1 text-[#718096]"
          >
            <Sparkles className="w-5 h-5" />
            <span className="text-xs">Home</span>
          </Link>
          <Link 
            to="/history"
            className="flex flex-col items-center gap-1 text-[#718096]"
          >
            <History className="w-5 h-5" />
            <span className="text-xs">History</span>
          </Link>
          <Link 
            to="/favorites"
            className="flex flex-col items-center gap-1 text-[#3090A8]"
          >
            <Heart className="w-5 h-5" />
            <span className="text-xs">Favorites</span>
          </Link>
        </div>
      </nav>
      <SiteFooter />
    </div>
  );
};

export default FavoritesPage;
